import { Form, FormItemProps } from "antd";
import { ReactNode } from "react";
import { styled } from "styled-components";
import { colors } from "../../../theme/colors";

export const card = colors.isDark
  ? {
      bg: "#0E1B2D",
      innerBg: "#142337",
      border: "#22324A",
      divider: "#1C2B43",
      iconBg: "#152538",
      iconBorder: "#2A3D5C",
      shadow:
        "0 1px 3px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.30)",
      accentSoft: "rgba(120, 170, 230, 0.08)",
      accentHover: "rgba(120, 170, 230, 0.14)",
    }
  : {
      bg: "#F4F7FB",
      innerBg: "#FFFFFF",
      border: "#DCE4EF",
      divider: "#E4EAF3",
      iconBg: "#FFFFFF",
      iconBorder: "#D8E0EC",
      shadow:
        "0 1px 3px rgba(15, 32, 64, 0.04), 0 1px 2px rgba(15, 32, 64, 0.03)",
      accentSoft: "rgba(32, 84, 150, 0.06)",
      accentHover: "rgba(32, 84, 150, 0.10)",
    };

const SectionShell = styled.section<{ $fillHeight?: boolean }>`
  width: 100%;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  padding: 2.4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  box-shadow: ${card.shadow};
  ${({ $fillHeight }) =>
    $fillHeight
      ? `
    flex: 1;
    min-height: 0;
    height: 100%;
  `
      : ""}

  && .ant-form-item,
  && .ant-form-item-has-error,
  && .ant-form-item-has-warning {
    margin-bottom: 0;
  }

  & .ant-form-item-label {
    padding: 0 0 0.6rem 0;
  }

  & .ant-form-item-label > label {
    height: auto;
    font-size: 1.3rem;
    font-weight: 500;
    color: ${colors.text.hex};
  }

  & .ant-form-item-label > label::after {
    display: none;
  }

  & .ant-form-item-extra {
    font-size: 1.15rem;
    color: ${colors.text.hex};
    opacity: 0.55;
    margin-top: 0.5rem;
    line-height: 1.4;
  }

  & .ant-form-item-explain {
    margin-top: 0.4rem;
    font-size: 1.2rem;
  }

  & .ant-input,
  & .ant-input-number,
  & .ant-picker,
  & .ant-select-selector,
  & .ant-input-affix-wrapper,
  & .ant-input-number-affix-wrapper {
    border-radius: 0.6rem !important;
    background-color: ${card.innerBg} !important;
    border-color: ${card.border} !important;
    color: ${colors.text.hex} !important;
  }

  & .ant-input::placeholder,
  & .ant-input-number-input::placeholder,
  & .ant-select-selection-placeholder,
  & .ant-picker-input > input::placeholder {
    color: ${colors.text.hex} !important;
    opacity: 0.4 !important;
  }

  & .ant-input:hover,
  & .ant-input-number:hover,
  & .ant-picker:hover,
  & .ant-select:hover .ant-select-selector,
  & .ant-input-affix-wrapper:hover,
  & .ant-input-number-affix-wrapper:hover {
    border-color: ${colors.primary.hex} !important;
  }

  & .ant-input:focus,
  & .ant-input-focused,
  & .ant-input-number-focused,
  & .ant-picker-focused,
  & .ant-select-focused .ant-select-selector,
  & .ant-input-affix-wrapper-focused,
  & .ant-input-number-affix-wrapper-focused {
    border-color: ${colors.primary.hex} !important;
    box-shadow: 0 0 0 2px ${card.accentSoft} !important;
  }

  & .ant-select,
  & .ant-picker,
  & .ant-input,
  & .ant-input-number,
  & .ant-input-affix-wrapper,
  & .ant-input-textarea,
  & .ant-input-number-affix-wrapper {
    width: 100%;
  }

  & textarea.ant-input {
    width: 100%;
    display: block;
    resize: vertical;
  }

  & .ant-select-selection-item,
  & .ant-picker-input > input,
  & .ant-input-number-input {
    color: ${colors.text.hex} !important;
  }

  & .ant-select-arrow,
  & .ant-picker-suffix {
    color: ${colors.text.hex} !important;
    opacity: 0.55;
  }

  & .ant-form-item-control,
  & .ant-form-item-control-input,
  & .ant-form-item-control-input-content {
    min-width: 0;
  }
`;

const SectionHeader = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 1.4rem;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid ${card.divider};
`;

const SectionIcon = styled.div`
  flex: 0 0 auto;
  width: 4rem;
  height: 4rem;
  border-radius: 0.9rem;
  background: ${card.iconBg};
  border: 1px solid ${card.iconBorder};
  color: ${colors.primary.hex};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.7rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
`;

const SectionSubtitle = styled.p`
  margin: 0.3rem 0 0 0;
  font-size: 1.3rem;
  font-weight: 400;
  color: ${colors.text.hex};
  opacity: 0.65;
  line-height: 1.5;
`;

export const Section = ({
  icon,
  title,
  subtitle,
  children,
  fillHeight,
  action,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
  fillHeight?: boolean;
  action?: ReactNode;
}) => (
  <SectionShell $fillHeight={fillHeight}>
    <SectionHeader>
      <SectionIcon>{icon}</SectionIcon>
      <div style={{ flex: 1, minWidth: 0 }}>
        <SectionTitle>{title}</SectionTitle>
        {subtitle ? <SectionSubtitle>{subtitle}</SectionSubtitle> : null}
      </div>
      {action ? (
        <div style={{ flex: "0 0 auto", marginLeft: "0.8rem" }}>{action}</div>
      ) : null}
    </SectionHeader>
    {children}
  </SectionShell>
);

export const FieldGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $cols = 2 }) => $cols},
    minmax(0, 1fr)
  );
  gap: 1.6rem 2rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 36rem) {
    grid-template-columns: ${({ $cols = 2 }) =>
      $cols >= 3 ? "repeat(2, minmax(0, 1fr))" : "1fr"};
  }

  @container (max-width: 22rem) {
    grid-template-columns: 1fr;
  }
`;

const FieldShell = styled.div<{ $fullWidth?: boolean }>`
  ${({ $fullWidth }) => ($fullWidth ? "grid-column: 1 / -1;" : "")}
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
  width: 100%;
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.2;

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.5rem;
    opacity: 0.85;
  }
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const FieldHelper = styled.div`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.4;
`;

const FieldControl = styled.div`
  width: 100%;
  min-width: 0;

  & .ant-form-item {
    margin-bottom: 0;
  }

  & .ant-form-item-label {
    display: none;
  }
`;

type FieldProps = {
  name?: FormItemProps["name"];
  label: string;
  icon?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
  rules?: FormItemProps["rules"];
  fullWidth?: boolean;
  children: ReactNode;
} & Omit<
  FormItemProps,
  "name" | "label" | "rules" | "children" | "required" | "extra"
>;

export const Field = ({
  name,
  label,
  icon,
  helper,
  required,
  rules,
  fullWidth,
  children,
  ...rest
}: FieldProps) => (
  <FieldShell $fullWidth={fullWidth}>
    <FieldLabel>
      {icon}
      <span>
        {label}
        {required ? <RequiredMark>*</RequiredMark> : null}
      </span>
    </FieldLabel>
    <FieldControl>
      <Form.Item {...rest} name={name} rules={rules}>
        {children}
      </Form.Item>
    </FieldControl>
    {helper ? <FieldHelper>{helper}</FieldHelper> : null}
  </FieldShell>
);

const RatingShell = styled.div<{ $filled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.6rem;
  background-color: ${card.innerBg};
  border: 1px solid
    ${({ $filled }) => ($filled ? colors.primary.hex : card.border)};
  border-radius: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  min-width: 0;

  &:hover {
    border-color: ${colors.primary.hex};
    box-shadow: 0 2px 8px rgba(32, 84, 150, 0.06);
  }

  & .ant-form-item {
    margin-bottom: 0;
  }
`;

const RatingHead = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RatingIcon = styled.div<{ $filled?: boolean }>`
  flex: 0 0 auto;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 0.7rem;
  background: ${({ $filled }) => ($filled ? colors.primary.hex : card.bg)};
  border: ${({ $filled }) =>
    $filled ? "1px solid transparent" : `1px solid ${card.border}`};
  color: ${({ $filled }) =>
    $filled ? colors.white.hex : colors.primary.hex};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
`;

const RatingTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
  line-height: 1.3;
`;

const RatingHint = styled.div`
  font-size: 1.15rem;
  font-weight: 400;
  color: ${colors.text.hex};
  opacity: 0.6;
  margin-top: 0.2rem;
  line-height: 1.4;
`;

export const RatingTile = ({
  icon,
  title,
  hint,
  filled,
  children,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  filled?: boolean;
  children: ReactNode;
}) => (
  <RatingShell $filled={filled}>
    <RatingHead>
      <RatingIcon $filled={filled}>{icon}</RatingIcon>
      <div style={{ minWidth: 0 }}>
        <RatingTitle>{title}</RatingTitle>
        {hint ? <RatingHint>{hint}</RatingHint> : null}
      </div>
    </RatingHead>
    {children}
  </RatingShell>
);

export const HelperRow = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;

  & > span:last-child {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
  }
`;

export const DurationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  min-width: 0;

  & .ant-form-item {
    flex: 1 1 0;
    min-width: 0;
  }

  & .ant-input,
  & .ant-input-number {
    width: 100%;
    text-align: center;
  }

  & .ant-input-number-input {
    text-align: center;
  }

  & .duration-separator {
    flex: 0 0 auto;
    font-size: 1.8rem;
    font-weight: 600;
    color: ${colors.text.hex};
    opacity: 0.4;
  }
`;
